/**
 * Tokenizer-based highlighting for the chat transcript (fenced code), not the
 * Files editor — that mounts `@tavsin/editor`. highlight.js is bundled locally
 * (no CDN, CSP-safe). We register a curated language set rather than all ~190
 * to keep the bundle lean. Unknown languages fall back to plain, escaped text.
 */
import hljs from "highlight.js/lib/core";
import bash from "highlight.js/lib/languages/bash";
import c from "highlight.js/lib/languages/c";
import cpp from "highlight.js/lib/languages/cpp";
import csharp from "highlight.js/lib/languages/csharp";
import css from "highlight.js/lib/languages/css";
import diff from "highlight.js/lib/languages/diff";
import dockerfile from "highlight.js/lib/languages/dockerfile";
import go from "highlight.js/lib/languages/go";
import ini from "highlight.js/lib/languages/ini";
import java from "highlight.js/lib/languages/java";
import javascript from "highlight.js/lib/languages/javascript";
import json from "highlight.js/lib/languages/json";
import kotlin from "highlight.js/lib/languages/kotlin";
import less from "highlight.js/lib/languages/less";
import lua from "highlight.js/lib/languages/lua";
import markdown from "highlight.js/lib/languages/markdown";
import objectivec from "highlight.js/lib/languages/objectivec";
import perl from "highlight.js/lib/languages/perl";
import php from "highlight.js/lib/languages/php";
import python from "highlight.js/lib/languages/python";
import ruby from "highlight.js/lib/languages/ruby";
import rust from "highlight.js/lib/languages/rust";
import scss from "highlight.js/lib/languages/scss";
import shell from "highlight.js/lib/languages/shell";
import sql from "highlight.js/lib/languages/sql";
import swift from "highlight.js/lib/languages/swift";
import toml from "highlight.js/lib/languages/ini"; // TOML shares the ini grammar
import typescript from "highlight.js/lib/languages/typescript";
import xml from "highlight.js/lib/languages/xml";
import yaml from "highlight.js/lib/languages/yaml";

const LANGS: Record<string, unknown> = {
  bash, c, cpp, csharp, css, diff, dockerfile, go, ini, java, javascript, json,
  kotlin, less, lua, markdown, objectivec, perl, php, python, ruby, rust, scss,
  shell, sql, swift, toml, typescript, xml, yaml,
};
for (const [name, def] of Object.entries(LANGS)) {
  hljs.registerLanguage(name, def as never);
}

/** Map a file extension, fence language, or bare filename to a registered highlight.js language. */
export function extToLang(extOrName: string): string {
  const e = extOrName.toLowerCase();
  const map: Record<string, string> = {
    ...Object.fromEntries(Object.keys(LANGS).map((name) => [name, name])),
    ts: "typescript", tsx: "typescript", mts: "typescript", cts: "typescript",
    js: "javascript", jsx: "javascript", mjs: "javascript", cjs: "javascript",
    json: "json", jsonc: "json",
    py: "python", pyi: "python",
    rs: "rust",
    go: "go",
    c: "c", h: "c",
    cpp: "cpp", cc: "cpp", cxx: "cpp", "c++": "cpp", hpp: "cpp", hh: "cpp", hxx: "cpp", ipp: "cpp",
    m: "objectivec", mm: "objectivec",
    java: "java",
    kt: "kotlin", kts: "kotlin",
    swift: "swift",
    cs: "csharp",
    php: "php",
    rb: "ruby", gemfile: "ruby", rake: "ruby",
    lua: "lua",
    pl: "perl", pm: "perl",
    sh: "bash", bash: "bash", zsh: "bash", ksh: "bash",
    sql: "sql",
    yml: "yaml", yaml: "yaml",
    toml: "toml", ini: "ini", cfg: "ini", conf: "ini", env: "ini",
    html: "xml", htm: "xml", xml: "xml", svg: "xml", vue: "xml", xhtml: "xml",
    css: "css",
    scss: "scss", sass: "scss",
    less: "less",
    md: "markdown", markdown: "markdown", mdx: "markdown",
    diff: "diff", patch: "diff",
    dockerfile: "dockerfile",
  };
  return map[e] ?? "";
}

/**
 * Highlight `code` for the given extension/filename, returning HTML with
 * `hljs-*` token spans (already escaped). Unknown languages return plain
 * escaped text so a `.txt`/`.log` renders verbatim.
 */
export function highlightCode(code: string, extOrName: string): string {
  const lang = extToLang(extOrName);
  if (!lang) return escapeHtml(code);
  try {
    return hljs.highlight(code, { language: lang, ignoreIllegals: true }).value;
  } catch {
    return escapeHtml(code);
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>]/g, (c) => (c === "&" ? "&amp;" : c === "<" ? "&lt;" : "&gt;"));
}
